import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { apiFetch } from "@/services/api-client";
import { NEW_BUILD_EVENT } from "@/lib/chunk-reload";

/** How often an open tab asks whether a newer build is served, besides each time it is focused. */
const CHECK_MS = 5 * 60 * 1000;

/**
 * The app's entry script as a page names it. A build names it by a hash of its contents
 * (`/assets/index-<hash>.js`), so a different name means a different build.
 */
function entryOf(page: ParentNode): string | null {
  const src = page
    .querySelector('script[type="module"][src]')
    ?.getAttribute("src");
  return src === null || src === undefined
    ? null
    : new URL(src, window.location.origin).pathname;
}

/** Whether the server now serves a build other than the one this tab runs. */
async function newerServed(): Promise<boolean> {
  const running = entryOf(document);
  if (running === null) return false;
  try {
    const response = await apiFetch("/", { cache: "no-store" });
    const page = new DOMParser().parseFromString(
      await response.text(),
      "text/html",
    );
    const served = entryOf(page);
    return served !== null && served !== running;
  } catch {
    // Offline or between deploys: the next check asks again.
    return false;
  }
}

/**
 * After a deploy an open tab runs the old app until it is reloaded. This says so, and reloads
 * only when asked: the attorney may have typing that is not saved yet. It says so at once, too,
 * when a chunk of the old build failed to load and the page held off reloading over that typing.
 *
 * Drawn in the flow of the layout that holds it, in a row of its own under the page, never
 * floated over the page: floated, it covered whatever control sat at the page's foot.
 */
export function NewVersionNotice() {
  const [available, setAvailable] = useState(false);
  useEffect(() => {
    if (available) return undefined;
    const check = () => {
      void newerServed().then((newer) => {
        if (newer) setAvailable(true);
      });
    };
    const told = () => {
      setAvailable(true);
    };
    window.addEventListener("focus", check);
    window.addEventListener(NEW_BUILD_EVENT, told);
    const every = window.setInterval(check, CHECK_MS);
    return () => {
      window.removeEventListener("focus", check);
      window.removeEventListener(NEW_BUILD_EVENT, told);
      window.clearInterval(every);
    };
  }, [available]);

  if (!available) return null;
  return (
    <Panel
      tone="info"
      role="status"
      shape="row"
      pad="tight"
      className="mx-4 my-2 flex flex-wrap items-center justify-center gap-3"
    >
      <span className="text-sm">
        A new version of Praxis is available. Reload to use it.
      </span>
      <Button
        size="dense"
        onClick={() => {
          window.location.reload();
        }}
      >
        Reload
      </Button>
    </Panel>
  );
}
